import InputAutoCompleteMultipleWithAccessor from '../input-autocomplete-multiple-with-accessors/input-autocomplete-multiple-with-accessors';
import InputAutoCompleteMultiple from '../input-autocomplete-multiple/input-autocomplete-multiple';
import InputAutoCompleteSimpleWithAccessors from '../input-autocomplete-simple-with-accessors/input-autocomplete-simple-with-accessors';
import { GroupedTitleI } from '../input-autocomplete-simple-with-accessors/input-autocomplete-simple-with-accessors.interface';
import InputAutoCompleteSimple from '../input-autocomplete-simple/input-autocomplete-simple';
import { TextFieldProps } from '../text-field/textField';

// const MIN_OPTION_HEIGHT = 32;
const MIN_OPTION_VISIBLE = 5;
// const ACCORDION_PADDING = 8; // relative to class py-2
export interface InputAutocompleteProps<T = string> extends Omit<TextFieldProps, 'onChange' | 'value'> {
    options: T[];
    accessorLabel?: string;
    accessorValue?: string;
    minVisible?: number;
    debounceTime?: number;
    callback?(value: string): void;
    onChange?(value: string): void;
    onInputChange?(value: string): void;
    onAllSelected?(value: string[]): void;
    selectAllOption?: boolean;
    onScrollToEnd?(): void;
    filterRemote?: boolean;
    isLoading?: boolean;
    name: string;
    label?: string;
    optionDropdownClasses?: string;
    labelClasses?: string;
    inputAutoCompleteOptionClassName?: string;
    inputClassName?: string;
    containerClassNames?: string;
    currentPage?: number; // add to handle on scroll event
    'data-testid'?: string;
    'data-test-id'?: string;
    id?: string;
    multiple?: boolean;
    wrapPills?: boolean;
    pillCount?: number;
    value?: string | string[];
    enableEmptyOption?: boolean;
    emptyOptionLabel?: string;
    itemNotFoundErrorMessage?: string;
    itemNotFoundError?: boolean;
    placeholder?: string;
    disabled?: boolean;
    required?: boolean;
    accessorGroup?: T extends string ? never : Extract<keyof T, string>;
    grouped?: T extends string ? never : boolean;
    groupedTitles?: GroupedTitleI[];
    valueUpdateable?: boolean;
    disableSelection?: boolean;
    bypassGetLabelFromValue?: boolean;
    onClick?: () => void; // to handle click on the input
    onFocus?: () => void; // to handle focus on the input
    onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void; // to handle blur on the input
    hasDeviceField?: string; // Campo que indica si el dispositivo está reportando
    noDeviceMessage?: string; // Mensaje que se muestra si no hay dispositivos reportando
    tachographIcon?: React.ReactNode | ((option: T) => React.ReactNode); // Icono personalizado para tacografos
    position?: 'top' | 'bottom'; // Posición del dropdown: arriba o abajo del input
    singleSelection?: boolean; // Para permitir selección única en el multiple autocomplete
}

export const InputAutocomplete = <T,>(props: InputAutocompleteProps<T>) => {
    const { multiple = false } = props;

    if (multiple) {
        return renderMultipleAutocomplete(props);
    }

    return renderSimpleAutocomplete(props);
};

// Helper functions to create specific autocomplete instances
export const SimpleAutocomplete = <T,>(props: Omit<InputAutocompleteProps<T>, 'multiple'>) => {
    return renderSimpleAutocomplete({ ...props, multiple: false });
};

export const MultipleAutocomplete = <T,>(props: Omit<InputAutocompleteProps<T>, 'multiple'>) => {
    return renderMultipleAutocomplete({ ...props, multiple: true });
};

const renderMultipleAutocomplete = <T,>({
    options,
    accessorLabel,
    accessorValue,
    minVisible = MIN_OPTION_VISIBLE,
    optionDropdownClasses,
    labelClasses,
    inputAutoCompleteOptionClassName,
    debounceTime = 3000,
    callback,
    onInputChange,
    onScrollToEnd,
    filterRemote = false,
    isLoading,
    name,
    label,
    containerClassNames,
    wrapPills = false,
    pillCount = 3,
    itemNotFoundErrorMessage,
    selectAllOption,
    onAllSelected,
    placeholder,
    valueUpdateable = false,
    disableSelection = false,
    disabled = false,
    required = false,
    hasDeviceField,
    noDeviceMessage,
    tachographIcon,
    ...props
}: InputAutocompleteProps<T>) => {
    if (accessorLabel && accessorValue) {
        return (
            <InputAutoCompleteMultipleWithAccessor
                optionClasses={inputAutoCompleteOptionClassName}
                inputClassName={props.inputClassName}
                optionDropdownClasses={optionDropdownClasses}
                minVisible={minVisible}
                debounceTime={debounceTime}
                label={label}
                containerClasses={containerClassNames}
                labelClasses={labelClasses}
                name={name}
                options={options}
                onChange={props.onChange}
                onInputChange={onInputChange}
                isLoading={isLoading}
                onScrollToEnd={onScrollToEnd}
                callback={callback}
                filterRemote={filterRemote}
                itemNotFoundErrorMessage={itemNotFoundErrorMessage}
                itemNotFoundError={false}
                accessorValue={accessorValue}
                accessorLabel={accessorLabel}
                value={props.value as string[]}
                wrapPills={wrapPills}
                pillCount={pillCount}
                selectAllOption={selectAllOption}
                onAllSelected={onAllSelected}
                placeholder={!disabled ? placeholder : ''}
                valueUpdateable={valueUpdateable}
                disableSelection={disableSelection}
                disabled={disabled}
                required={required}
                hasDeviceField={hasDeviceField}
                noDeviceMessage={noDeviceMessage}
                tachographIcon={tachographIcon}
                singleSelection={props.singleSelection}
            />
        );
    }

    return (
        <InputAutoCompleteMultiple
            optionClasses={inputAutoCompleteOptionClassName}
            optionDropdownClasses={optionDropdownClasses}
            minVisible={minVisible}
            debounceTime={debounceTime}
            label={label}
            containerClasses={containerClassNames}
            labelClasses={labelClasses}
            name={name}
            options={options as unknown as string[]}
            onChange={props.onChange}
            onInputChange={onInputChange}
            isLoading={isLoading}
            onScrollToEnd={onScrollToEnd}
            callback={callback}
            filterRemote={filterRemote}
            itemNotFoundErrorMessage={itemNotFoundErrorMessage}
            itemNotFoundError={false}
            value={props.value as string[]}
            wrapPills={wrapPills}
            pillCount={pillCount}
            selectAllOption={selectAllOption}
            onAllSelected={onAllSelected}
            placeholder={!disabled ? placeholder : ''}
            disabled={disabled}
            required={required}
        />
    );
};

const renderSimpleAutocomplete = <T,>({
    options,
    accessorLabel,
    accessorValue,
    minVisible = MIN_OPTION_VISIBLE,
    optionDropdownClasses,
    inputAutoCompleteOptionClassName,
    debounceTime = 3000,
    callback,
    onInputChange,
    onScrollToEnd,
    filterRemote = false,
    isLoading,
    name,
    label,
    containerClassNames,
    enableEmptyOption = false,
    emptyOptionLabel = '',
    itemNotFoundError = false,
    itemNotFoundErrorMessage,
    placeholder,
    disabled = false,
    required = false,
    accessorGroup,
    grouped,
    groupedTitles,
    position = 'bottom',
    id,
    onClick,
    onFocus,
    onBlur,
    ...props
}: InputAutocompleteProps<T>) => {
    if (accessorLabel && accessorValue) {
        return (
            <InputAutoCompleteSimpleWithAccessors
                optionClasses={inputAutoCompleteOptionClassName}
                optionDropdownClasses={optionDropdownClasses}
                minVisible={minVisible}
                debounceTime={debounceTime}
                label={label}
                containerClasses={containerClassNames}
                inputClassName={props.inputClassName}
                name={name}
                options={options as unknown as Record<string, unknown>[]}
                onChange={props.onChange}
                onInputChange={onInputChange}
                isLoading={isLoading}
                onScrollToEnd={onScrollToEnd}
                callback={callback}
                filterRemote={filterRemote}
                itemNotFoundErrorMessage={itemNotFoundErrorMessage}
                itemNotFoundError={itemNotFoundError}
                accessorValue={accessorValue}
                accessorLabel={accessorLabel}
                value={props.value as string}
                emptyOptionLabel={emptyOptionLabel}
                enableEmptyOption={enableEmptyOption}
                placeholder={!disabled ? placeholder : ''}
                disabled={disabled}
                required={required}
                grouped={grouped}
                accessorGroup={accessorGroup}
                groupedTitles={groupedTitles}
                position={position}
                id={id}
                onClick={onClick}
                onFocus={onFocus}
                onBlur={onBlur}
                bypassGetLabelFromValue={props.bypassGetLabelFromValue}
            />
        );
    }

    return (
        <InputAutoCompleteSimple
            optionClasses={inputAutoCompleteOptionClassName}
            optionDropdownClasses={optionDropdownClasses}
            minVisible={minVisible}
            debounceTime={debounceTime}
            label={label}
            containerClasses={containerClassNames}
            name={name}
            options={options as unknown as string[]}
            onChange={props.onChange}
            onInputChange={onInputChange}
            isLoading={isLoading}
            onScrollToEnd={onScrollToEnd}
            callback={callback}
            filterRemote={filterRemote}
            itemNotFoundErrorMessage={itemNotFoundErrorMessage}
            itemNotFoundError={itemNotFoundError}
            value={props.value as string}
            emptyOptionLabel={emptyOptionLabel}
            enableEmptyOption={enableEmptyOption}
            placeholder={!disabled ? placeholder : ''}
            disabled={disabled}
            required={required}
            position={position}
            onClick={onClick}
            onFocus={onFocus}
        />
    );
};

InputAutocomplete.defaultProps = {};

export default InputAutocomplete;
